import React, { useState, useEffect } from 'react';
import { familleTypeArticleService } from '../../../services/familleTypeArticleService.js';
import Card from '../../common/Card.jsx';
import Pagination from '../../common/Pagination.jsx';
import FamilleTypeArticleList from './FamilleTypeArticle/FamilleTypeArticleList';
import AddFamilleTypeArticleModal from './FamilleTypeArticle/AddFamilleTypeArticleModal';
import EditFamilleTypeArticleModal from './FamilleTypeArticle/EditFamilleTypeArticleModal';
import ViewFamilleTypeArticleModal from './FamilleTypeArticle/ViewFamilleTypeArticleModal';
import DeleteFamilleTypeArticleModal from './FamilleTypeArticle/DeleteFamilleTypeArticleModal';
import { useTheme } from '../../../contexts/ThemeContext';

const FamilleTypeArticle = () => {
  const [familleTypeArticles, setFamilleTypeArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    searchTerms: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFamilleTypeArticle, setSelectedFamilleTypeArticle] = useState(null);

  // Mobile menu state
  const [showMobileActions, setShowMobileActions] = useState(false);

  // Load famille type articles from API with filters
  const loadFamilleTypeArticles = async (pageSize = pagination.pageSize, page = pagination.page, filterParams = filters) => {
    setLoading(true);
    setError('');
    
    try {
      // Prepare filter parameters for API
      const apiFilters = {};
      if (filterParams.searchTerms?.trim()) apiFilters.searchTerms = filterParams.searchTerms.trim();

      const result = await familleTypeArticleService.getFamilleTypeArticles(pageSize, page, apiFilters);
      
      if (result.success) {
        setFamilleTypeArticles(result.data.list);
        setPagination({
          page: result.data.page,
          pageSize: result.data.pageSize,
          total: result.data.total,
          maxPage: result.data.maxPage
        });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors du chargement des familles de types d\'articles');
    } finally {
      setLoading(false);
    }
  };

  // Export functionality - using existing table data only
  const handleExport = (format = 'csv') => {
    if (!familleTypeArticles || familleTypeArticles.length === 0) {
      setError('Aucune donnée à exporter');
      return;
    }

    setExporting(true);
    
    try {
      if (format === 'csv') {
        exportToCSV(familleTypeArticles);
      } else if (format === 'excel') {
        exportToExcel(familleTypeArticles);
      } else if (format === 'json') {
        exportToJSON(familleTypeArticles);
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Erreur lors de l\'exportation des données');
    } finally {
      setExporting(false);
    }
  };

  // Export to CSV
  const exportToCSV = (data) => {
    const headers = [
      'ID',
      'Désignation'
    ];

    const csvContent = [
      headers.join(','),
      ...data.map(familleTypeArticle => [
        familleTypeArticle.id || '',
        `"${(familleTypeArticle.designation || '').replace(/"/g, '""')}"""`
      ].join(','))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `familles-types-articles-export-${timestamp}.csv`, 'text/csv;charset=utf-8;');
  };

  // Export to JSON
  const exportToJSON = (data) => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalRecords: data.length,
      pagination: {
        currentPage: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: pagination.maxPage,
        totalRecords: pagination.total
      },
      filters: filtersApplied ? filters : null,
      data: data
    };
    
    const jsonContent = JSON.stringify(exportData, null, 2);
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(jsonContent, `familles-types-articles-export-${timestamp}.json`, 'application/json');
  };

  // Export to Excel
  const exportToExcel = (data) => {
    const headers = [
      'ID',
      'Désignation'
    ];

    // Excel-compatible CSV with BOM for proper encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join('\t'),
      ...data.map(familleTypeArticle => [
        familleTypeArticle.id || '',
        (familleTypeArticle.designation || '').replace(/\t/g, ' ')
      ].join('\t'))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `familles-types-articles-export-${timestamp}.xlsx`, 'application/vnd.ms-excel');
  };

  // Download file utility
  const downloadFile = (content, filename, mimeType) => {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setTimeout(() => {
        console.log(`Export réussi: ${filename}`);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      setError('Erreur lors du téléchargement du fichier');
    }
  };
const { isDarkMode } = useTheme();
  // Load famille type articles on component mount
  useEffect(() => {
    loadFamilleTypeArticles();
  }, []);

  // Full right-content loading indicator while API responds (keeps hooks order)
  if (loading) {
    return (
      <div className="p-4 sm:p-6 min-h-screen flex items-start justify-center">
        <div className="w-full max-w-6xl">
          <div className={`rounded-2xl shadow-sm border p-8 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minHeight: '60vh' }}>
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`} />
            <div className={`mt-4 text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Chargement des familles de types d'articles...</div>
            <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Veuillez patienter pendant la récupération des données.</div>
          </div>
        </div>
      </div>
    );
  }

  // Handle page change
  const handlePageChange = (newPage) => {
    loadFamilleTypeArticles(pagination.pageSize, newPage, filters);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    loadFamilleTypeArticles(newPageSize, 1, filters);
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setFiltersApplied(Object.values(filters).some(value => value.toString().trim() !== ''));
    loadFamilleTypeArticles(pagination.pageSize, 1, filters);
    setShowFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      searchTerms: ''
    };
    setFilters(clearedFilters);
    setFiltersApplied(false);
    loadFamilleTypeArticles(pagination.pageSize, 1, clearedFilters);
    setShowFilters(false);
  };

  // Modal handlers
  const handleView = (familleTypeArticle) => {
    setSelectedFamilleTypeArticle(familleTypeArticle);
    setShowViewModal(true);
  };

  const handleEdit = (familleTypeArticle) => {
    setSelectedFamilleTypeArticle(familleTypeArticle);
    setShowEditModal(true);
  };

  const handleDelete = (familleTypeArticle) => {
    setSelectedFamilleTypeArticle(familleTypeArticle);
    setShowDeleteModal(true);
  };

  const handleRefresh = () => {
    loadFamilleTypeArticles(pagination.pageSize, pagination.page, filters);
  };

  // Theme context
  

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className={`text-2xl sm:text-3xl font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Familles de Types d'Articles
          </h1>
          <p className={`text-sm sm:text-base mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Gestion des familles de types d'articles (RIA, Extincteur, PIA, AUTO, UMM...)
            {filtersApplied && (
              <span className={`font-medium ${isDarkMode ? 'text-dark-primary-400' : 'text-primary-600'}`}> (Filtré)</span>
            )}
          </p>
        </div>
        
        {/* Desktop Actions */}
        <div className="hidden lg:flex space-x-3 flex-shrink-0">
          <button 
            onClick={handleRefresh}
            disabled={loading}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={`fas fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              filtersApplied 
                ? 'bg-primary-50 border-primary-300 text-primary-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>
          

          
          <button 
            onClick={() => setShowAddModal(true)}
            className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${
              isDarkMode 
                ? 'bg-dark-primary-600 text-white hover:bg-dark-primary-700' 
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            <i className="fas fa-plus mr-2"></i>
            Nouvelle Famille
          </button>
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden">
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium flex-1 sm:flex-none"
            >
              <i className="fas fa-plus mr-1 sm:mr-2"></i>
              <span className="hidden xs:inline">Nouvelle </span>Famille
            </button>
            
            <div className="relative">
              <button 
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="bg-white border border-gray-300 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>
              
              {showMobileActions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-2">
                    <button 
                      onClick={() => {
                        handleRefresh();
                        setShowMobileActions(false);
                      }}
                      disabled={loading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className={`fas fa-sync-alt mr-2 w-4 ${loading ? 'animate-spin' : ''}`}></i>
                      Actualiser
                    </button>
                    <button 
                      onClick={() => {
                        setShowFilters(!showFilters);
                        setShowMobileActions(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        filtersApplied 
                          ? 'text-primary-700 bg-primary-50' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <i className="fas fa-filter mr-2 w-4"></i>
                      Filtrer
                      {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
                    </button>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    <div className="px-4 py-1 text-xs text-gray-500">
                      Exporter {familleTypeArticles.length} famille{familleTypeArticles.length > 1 ? 's' : ''}
                    </div>
                    <button 
                      onClick={() => {
                        handleExport('csv');
                        setShowMobileActions(false);
                      }}
                      disabled={exporting || familleTypeArticles.length === 0}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className="fas fa-file-csv mr-2 w-4 text-green-600"></i>
                      {exporting ? 'Export...' : 'CSV'}
                    </button>
                    <button 
                      onClick={() => {
                        handleExport('excel');
                        setShowMobileActions(false);
                      }}
                      disabled={exporting || familleTypeArticles.length === 0}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className="fas fa-file-excel mr-2 w-4 text-green-700"></i>
                      {exporting ? 'Export...' : 'Excel'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`border px-4 py-3 sm:px-6 sm:py-4 rounded-lg ${
          isDarkMode 
            ? 'bg-red-900/50 border-red-800 text-red-300' 
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle mr-2 mt-0.5 flex-shrink-0"></i>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Export Progress */}
      {exporting && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 sm:px-6 sm:py-4 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-download mr-2 animate-pulse"></i>
            <span className="text-sm sm:text-base">Préparation du fichier en cours...</span>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-l-4 border-l-primary-500">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center mb-2 sm:mb-0">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <i className="fas fa-filter text-primary-600"></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Filtres de recherche
                  </h3>
                  <p className="text-sm text-gray-600">Affinez votre recherche de familles de types d'articles</p>
                </div>
              </div>
              
              <button
                onClick={() => setShowFilters(false)}
                className="self-end sm:self-auto text-gray-400 hover:text-gray-600 p-1"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <i className="fas fa-search mr-2 text-gray-400"></i>
                  Recherche par désignation
                </label>
                <input
                  type="text"
                  value={filters.searchTerms}
                  onChange={(e) => handleFilterChange('searchTerms', e.target.value)}
                  placeholder="RIA, Extincteur, PIA, AUTO, UMM..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                  }`}
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium order-2 sm:order-1"
              >
                <i className="fas fa-times mr-2"></i>
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors text-sm font-medium order-1 sm:order-2"
              >
                <i className="fas fa-search mr-2"></i>
                Appliquer les filtres
              </button>
            </div>

            {/* Active Filters Summary */}
            {filtersApplied && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600">Filtres actifs:</span>
                  {filters.searchTerms && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      <i className="fas fa-search mr-1"></i>
                      {filters.searchTerms}
                      <button
                        onClick={() => handleFilterChange('searchTerms', '')}
                        className="ml-1 text-primary-600 hover:text-primary-800"
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Famille Type Articles List */}
      <FamilleTypeArticleList
        familleTypeArticles={familleTypeArticles}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Modals */}
      <AddFamilleTypeArticleModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          handleRefresh();
        }}
      />

      <EditFamilleTypeArticleModal
        isOpen={showEditModal}
        familleTypeArticle={selectedFamilleTypeArticle}
        onClose={() => {
          setShowEditModal(false);
          setSelectedFamilleTypeArticle(null);
        }}
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedFamilleTypeArticle(null);
          handleRefresh();
        }}
      />

      <ViewFamilleTypeArticleModal
        isOpen={showViewModal}
        familleTypeArticle={selectedFamilleTypeArticle}
        onClose={() => {
          setShowViewModal(false);
          setSelectedFamilleTypeArticle(null);
        }}
      />

      <DeleteFamilleTypeArticleModal
        isOpen={showDeleteModal}
        familleTypeArticle={selectedFamilleTypeArticle}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedFamilleTypeArticle(null);
        }}
        onSuccess={() => {
          setShowDeleteModal(false);
          setSelectedFamilleTypeArticle(null);
          handleRefresh();
        }}
      />

      {/* Click outside to close mobile actions */}
      {showMobileActions && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMobileActions(false)}
        />
      )}
    </div>
  );
};

export default FamilleTypeArticle;